import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function NotifyPopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem('yb_notify_dismissed');
    
    // Auto-show after 3 seconds only if not dismissed
    let timer;
    if (!dismissed) {
      timer = setTimeout(() => {
        setShow(true);
      }, 3000);
    }

    // Always listen for manual trigger, even if previously dismissed
    const handleShowPopup = () => {
      console.log('NotifyPopup: showNotifyPopup event received');
      setShow(true);
    };
    window.addEventListener('showNotifyPopup', handleShowPopup);

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('showNotifyPopup', handleShowPopup);
    };
  }, []);

  const handleClose = () => {
    setShow(false);
    localStorage.setItem('yb_notify_dismissed', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;

    setLoading(true);
    try {
      await fetch(process.env.REACT_APP_GOOGLE_EMAIL_SIGNUP_SCRIPTS_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, timestamp: new Date().toISOString() }),
      });
      setSubmitted(true);
      localStorage.setItem('yb_notify_dismissed', 'true');
      setTimeout(() => setShow(false), 2500);
    } catch (err) {
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            zIndex: 10010,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              borderRadius: '4px',
              padding: '48px 36px 40px',
              maxWidth: '600px',
              width: '100%',
              textAlign: 'center',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255,255,255,0.1) inset',
            }}
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#333')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#999')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {!submitted ? (
              <>
                {/* Bell icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 15 }}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px',
                  }}
                >
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </motion.div>

                <h2 style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  fontFamily: "'Montserrat', sans-serif",
                  color: '#1a1a1a',
                  margin: '0 0 12px 0',
                  lineHeight: 1.3,
                }}>
                  Stay in the Loop
                </h2>

                <p style={{
                  fontSize: '14px',
                  fontFamily: "'Montserrat', sans-serif",
                  color: '#666',
                  lineHeight: 1.6,
                  margin: '0 0 28px 0',
                  maxWidth: '340px',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}>
                  Be the first to know when New Brands, New Products and New Features launch.
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      border: '1.5px solid #e0e0e0',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontFamily: "'Montserrat', sans-serif",
                      color: '#1a1a1a',
                      background: 'rgba(255,255,255,0.8)',
                      outline: 'none',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(26,26,26,0.06)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '14px 24px',
                      background: '#1a1a1a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: "'Montserrat', sans-serif",
                      letterSpacing: '0.5px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.background = '#333';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#1a1a1a';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {loading ? 'Signing up...' : 'Sign Up to be Notified'}
                  </button>
                </form>

                <button
                  onClick={handleClose}
                  style={{
                    marginTop: '16px',
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    fontSize: '13px',
                    fontFamily: "'Montserrat', sans-serif",
                    cursor: 'pointer',
                    padding: '4px 8px',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#666')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#999')}
                >
                  No thanks
                </button>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: '#1a1a1a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '700',
                  fontFamily: "'Montserrat', sans-serif",
                  color: '#1a1a1a',
                  margin: '0 0 8px 0',
                }}>
                  You're on the list!
                </h3>
                <p style={{
                  fontSize: '14px',
                  fontFamily: "'Montserrat', sans-serif",
                  color: '#666',
                  margin: 0,
                }}>
                  We'll keep you updated on everything new.
                </p>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default NotifyPopup;
